import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calculator, Clock, Users, Download, Edit2, Check, Save, FileText, History, Calendar, RefreshCw, Trash2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const PayrollTab = () => {
  const { token } = useAuth();
  const api = axios.create({ baseURL: API_URL, headers: { Authorization: `Bearer ${token}` } });

  const [employees, setEmployees] = useState([]);
  const [payrollSettings, setPayrollSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [payPeriod, setPayPeriod] = useState({ start: '', end: '' });
  const [payrollData, setPayrollData] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [editingHours, setEditingHours] = useState(null);
  const [saving, setSaving] = useState(false);
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showPayStubs, setShowPayStubs] = useState(false);
  const [payStubs, setPayStubs] = useState([]);
  const [deletingStub, setDeletingStub] = useState(null);
  const [stubFilter, setStubFilter] = useState('');
  const [yearFilter, setYearFilter] = useState('all');
  const [yearInitialized, setYearInitialized] = useState(false);

  // Settings editor
  const [editingSettings, setEditingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});

  const availableYears = useMemo(() => {
    const years = new Set();
    payrollHistory.forEach(p => {
      if (p.period_start) years.add(new Date(p.period_start).getFullYear());
      if (p.created_at) years.add(new Date(p.created_at).getFullYear());
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [payrollHistory]);

  const filteredPayrollHistory = useMemo(() => {
    if (yearFilter === 'all') return payrollHistory;
    return payrollHistory.filter(p => {
      const year = p.period_start ? new Date(p.period_start).getFullYear() : new Date(p.created_at).getFullYear();
      return year === parseInt(yearFilter);
    });
  }, [payrollHistory, yearFilter]);

  useEffect(() => {
    if (!yearInitialized && payrollHistory.length > 0) {
      const currentYear = new Date().getFullYear();
      if (availableYears.includes(currentYear)) setYearFilter(currentYear.toString());
      setYearInitialized(true);
    }
  }, [payrollHistory, availableYears, yearInitialized]);

  useEffect(() => { loadData(); loadPayrollHistory(); }, []);

  const loadData = async () => {
    try {
      const [empRes, settingsRes] = await Promise.all([
        api.get('/api/employees'),
        api.get('/api/payroll-settings')
      ]);
      const nonParent = empRes.data.filter(e => e.role !== 'parent');
      setEmployees(nonParent);
      setPayrollSettings(settingsRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadPayrollHistory = async () => {
    try {
      const res = await api.get('/api/payroll/history');
      setPayrollHistory(res.data || []);
    } catch (error) { console.error('Error loading payroll history:', error); }
  };

  const loadPayStubs = async () => {
    try {
      const res = await api.get('/api/pay-stubs/all');
      setPayStubs(res.data || []);
    } catch (error) { toast.error('Error al cargar talonarios'); }
  };

  const deletePayStub = async (stubId, employeeName) => {
    if (!window.confirm(`Eliminar talonario de ${employeeName}?`)) return;
    setDeletingStub(stubId);
    try {
      await api.delete(`/api/pay-stubs/${stubId}`);
      toast.success('Talonario eliminado');
      loadPayStubs();
    } catch (error) { toast.error('Error al eliminar talonario'); }
    finally { setDeletingStub(null); }
  };

  const filteredPayStubs = useMemo(() => {
    if (!stubFilter) return payStubs;
    const f = stubFilter.toLowerCase();
    return payStubs.filter(s =>
      s.employee_name?.toLowerCase().includes(f) || s.period_start?.includes(f) || s.period_end?.includes(f)
    );
  }, [payStubs, stubFilter]);

  const loadSavedPayroll = (run) => {
    setPayPeriod({ start: run.period_start, end: run.period_end });
    const savedData = (run.employees || []).map(emp => ({
      employee: { user_id: emp.user_id || emp.employee_id, name: emp.name || emp.employee_name, profile: { position: emp.position || '', hourly_rate: parseFloat(emp.rate) || 0, salary: emp.is_hourly ? 0 : parseFloat(emp.rate) || 0, payment_method: emp.payment_method || 'check' } },
      hoursWorked: parseFloat(emp.hours || emp.hours_worked) || 0,
      hourlyRate: emp.is_hourly ? (parseFloat(emp.rate) || 0) : 0,
      fixedSalary: emp.is_hourly ? 0 : (parseFloat(emp.rate) || 0),
      grossPay: parseFloat(emp.grossPay || emp.gross_pay) || 0,
      deductions: { ...(emp.hacienda > 0 ? { 'Hacienda': parseFloat(emp.hacienda) } : {}), ...(emp.ss > 0 ? { 'Seguro Social': parseFloat(emp.ss) } : {}), ...(emp.medicare > 0 ? { 'Medicare': parseFloat(emp.medicare) } : {}), ...(emp.otherDeductions > 0 ? { 'Retencion 10%': parseFloat(emp.otherDeductions) } : {}) },
      totalDeductions: parseFloat(emp.deductions || emp.total_deductions) || 0,
      netPay: parseFloat(emp.netPay || emp.net_pay) || 0,
      isContractor: emp.is_contractor || emp.type === 'contractor',
      isExemptFromDeduction: false,
      isHourly: emp.is_hourly || false,
      hasPayConfig: (parseFloat(emp.rate) || 0) > 0,
      paymentMethod: emp.payment_method || 'check'
    }));
    setPayrollData(savedData);
    setShowHistory(false);
    toast.success(`Nomina del ${run.period_start} al ${run.period_end} cargada`);
  };

  const fetchClockHours = async (userId) => {
    try {
      const response = await api.get('/api/clock/all', { params: { user_id: userId, start_date: payPeriod.start, end_date: payPeriod.end } });
      return (response.data || []).reduce((sum, entry) => sum + (entry.hours_worked || 0), 0);
    } catch (error) { return 0; }
  };

  const calculatePayroll = async () => {
    if (!payPeriod.start || !payPeriod.end) { toast.error('Selecciona el periodo de pago'); return; }
    setProcessing(true);
    try {
      const [empRes, settingsRes] = await Promise.all([api.get('/api/employees'), api.get('/api/payroll-settings')]);
      const freshEmployees = empRes.data.filter(e => e.role !== 'parent');
      const freshSettings = settingsRes.data;
      setEmployees(freshEmployees);
      setPayrollSettings(freshSettings);

      const results = await Promise.all(freshEmployees.map(async (emp) => {
        const profile = emp.profile || {};
        const hourlyRate = parseFloat(profile.hourly_rate) || 0;
        const fixedSalary = parseFloat(profile.salary) || 0;
        const isContractor = profile.worker_classification === 'contractor';
        const taxRetentionPercent = parseFloat(profile.tax_retention_percent) || 0;
        const clockHours = await fetchClockHours(emp.user_id);
        let grossPay = 0, hoursWorked = clockHours;
        const hasPayConfig = hourlyRate > 0 || fixedSalary > 0;
        if (hourlyRate > 0) grossPay = hourlyRate * hoursWorked;
        else if (fixedSalary > 0) grossPay = fixedSalary;

        let deductions = {}, totalDeductions = 0;
        if (grossPay > 0) {
          if (isContractor) {
            const d = grossPay * (freshSettings.contractor_percent || 10) / 100;
            deductions = { 'Retencion 10%': d }; totalDeductions = d;
          } else {
            const h = grossPay * taxRetentionPercent / 100;
            const ss = grossPay * (freshSettings.social_security_percent || 6.2) / 100;
            const m = grossPay * (freshSettings.medicare_percent || 1.45) / 100;
            deductions = { 'Hacienda': h, 'Seguro Social': ss, 'Medicare': m }; totalDeductions = h + ss + m;
          }
        }
        return { employee: emp, hoursWorked, hourlyRate, fixedSalary, grossPay, deductions, totalDeductions, netPay: grossPay - totalDeductions, isContractor, isExemptFromDeduction: false, isHourly: hourlyRate > 0, hasPayConfig, paymentMethod: profile.payment_method || 'check', taxRetentionPercent };
      }));

      const withActivity = results.filter(r => r.hoursWorked > 0 || r.fixedSalary > 0);
      if (withActivity.length === 0) toast.warning('No hay empleados con horas registradas en este periodo');
      setPayrollData(withActivity);
      toast.success(`Nomina calculada: ${withActivity.length} empleados con actividad`);
    } catch (error) { toast.error('Error al calcular nomina'); }
    finally { setProcessing(false); }
  };

  const updateEmployeeHours = (idx, newHours) => {
    setPayrollData(prev => {
      const updated = [...prev]; const item = updated[idx];
      item.hoursWorked = parseFloat(newHours) || 0;
      if (item.isHourly) {
        item.grossPay = item.hourlyRate * item.hoursWorked;
        let totalDed = 0;
        if (item.isExemptFromDeduction) { item.deductions = {}; totalDed = 0; }
        else if (item.isContractor) { const d = item.grossPay * (payrollSettings.contractor_percent || 10) / 100; item.deductions = { 'Retencion 10%': d }; totalDed = d; }
        else { const h = item.grossPay * (item.taxRetentionPercent || 0) / 100; const ss = item.grossPay * (payrollSettings.social_security_percent || 6.2) / 100; const m = item.grossPay * (payrollSettings.medicare_percent || 1.45) / 100; item.deductions = { 'Hacienda': h, 'Seguro Social': ss, 'Medicare': m }; totalDed = h + ss + m; }
        item.totalDeductions = totalDed; item.netPay = item.grossPay - totalDed;
      }
      return updated;
    });
    setEditingHours(null);
  };

  const updatePaymentMethod = (idx, method) => {
    setPayrollData(prev => { const u = [...prev]; u[idx].paymentMethod = method; return u; });
  };

  const toggleContractorExemption = (idx, isExempt) => {
    setPayrollData(prev => {
      const updated = [...prev]; const item = updated[idx]; item.isExemptFromDeduction = isExempt;
      if (item.grossPay > 0) {
        let totalDed = 0;
        if (isExempt) { item.deductions = {}; }
        else if (item.isContractor) { const d = item.grossPay * (payrollSettings.contractor_percent || 10) / 100; item.deductions = { 'Retencion 10%': d }; totalDed = d; }
        else { const h = item.grossPay * (item.taxRetentionPercent || 0) / 100; const ss = item.grossPay * (payrollSettings.social_security_percent || 6.2) / 100; const m = item.grossPay * (payrollSettings.medicare_percent || 1.45) / 100; item.deductions = { 'Hacienda': h, 'Seguro Social': ss, 'Medicare': m }; totalDed = h + ss + m; }
        item.totalDeductions = totalDed; item.netPay = item.grossPay - totalDed;
      }
      return updated;
    });
  };

  const savePayroll = async () => {
    if (payrollData.length === 0) { toast.error('No hay datos de nomina para guardar'); return; }
    setSaving(true);
    try {
      const totals = payrollData.reduce((acc, p) => ({ hours: acc.hours + (p.isHourly ? p.hoursWorked : 0), gross: acc.gross + p.grossPay, deductions: acc.deductions + p.totalDeductions, net: acc.net + p.netPay }), { hours: 0, gross: 0, deductions: 0, net: 0 });
      const payload = {
        period_start: payPeriod.start, period_end: payPeriod.end,
        employees: payrollData.map(p => ({
          employee_id: p.employee.id, user_id: p.employee.user_id, name: p.employee.name, employee_name: p.employee.name,
          type: p.isContractor ? 'contractor' : 'employee', is_contractor: p.isContractor, is_hourly: p.isHourly,
          hours: p.hoursWorked, hours_worked: p.hoursWorked, rate: p.isHourly ? p.hourlyRate : (p.employee.profile?.salary || 0),
          grossPay: p.grossPay, gross_pay: p.grossPay, hacienda: p.deductions['Hacienda'] || 0, ss: p.deductions['Seguro Social'] || 0,
          medicare: p.deductions['Medicare'] || 0, otherDeductions: p.deductions['Retencion 10%'] || 0,
          deductions: p.totalDeductions, total_deductions: p.totalDeductions, netPay: p.netPay, net_pay: p.netPay,
          payment_method: p.paymentMethod || 'check'
        })),
        totals
      };
      await api.post('/api/payroll/process', payload);
      toast.success('Nomina guardada exitosamente');
      loadPayrollHistory();
    } catch (error) { toast.error('Error al guardar nomina'); }
    finally { setSaving(false); }
  };

  const saveSettings = async () => {
    try {
      await api.put('/api/payroll-settings', settingsForm);
      setPayrollSettings(settingsForm);
      setEditingSettings(false);
      toast.success('Configuracion de nomina guardada');
    } catch (error) { toast.error('Error al guardar configuracion'); }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-ES') : 'N/A';

  if (loading) return <div className="p-8" data-testid="payroll-loading">Cargando...</div>;

  return (
    <div className="space-y-6" data-testid="payroll-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-1 h-6 bg-gradient-to-b from-orange-400 to-orange-600 rounded-full"></div>
            <h2 className="text-xl font-bold text-slate-800" data-testid="payroll-title">Procesar Nomina</h2>
          </div>
          <p className="text-sm text-slate-500 ml-5">Calcular pagos y deducciones basado en horas trabajadas</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-[140px]" data-testid="payroll-year-filter">
              <Calendar className="w-4 h-4 mr-2" /><SelectValue placeholder="Ano" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los anos</SelectItem>
              {availableYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => { setLoading(true); loadData(); loadPayrollHistory(); }} data-testid="payroll-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="outline" onClick={() => setShowHistory(!showHistory)} data-testid="payroll-toggle-history">
            <History className="w-4 h-4 mr-2" /> {showHistory ? 'Ocultar' : 'Ver'} Historial ({filteredPayrollHistory.length})
          </Button>
          <Button variant="outline" onClick={() => { setShowPayStubs(!showPayStubs); if (!showPayStubs) loadPayStubs(); }} data-testid="payroll-toggle-stubs">
            <Receipt className="w-4 h-4 mr-2" /> {showPayStubs ? 'Ocultar' : 'Gestionar'} Talonarios
          </Button>
        </div>
      </div>

      {/* Pay Stubs Management */}
      {showPayStubs && (
        <Card data-testid="pay-stubs-section">
          <CardHeader>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <CardTitle className="flex items-center gap-2"><Receipt className="w-5 h-5" /> Gestion de Talonarios</CardTitle>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Input placeholder="Buscar por empleado o periodo..." value={stubFilter} onChange={(e) => setStubFilter(e.target.value)} className="w-full sm:w-64" data-testid="stub-search" />
                <Button variant="outline" size="sm" onClick={loadPayStubs}><RefreshCw className="w-4 h-4" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredPayStubs.length === 0 ? (
              <div className="text-center py-8 text-slate-500"><Receipt className="w-12 h-12 mx-auto mb-2 opacity-30" /><p>No hay talonarios disponibles</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100"><tr><th className="text-left p-3">Empleado</th><th className="text-left p-3">Periodo</th><th className="text-right p-3">Horas</th><th className="text-right p-3">Bruto</th><th className="text-right p-3">Deducciones</th><th className="text-right p-3">Neto</th><th className="text-left p-3">Metodo</th><th className="text-left p-3">Creado</th><th className="text-center p-3">Acciones</th></tr></thead>
                  <tbody>{filteredPayStubs.map((stub) => (
                    <tr key={stub.id} className="border-b hover:bg-slate-50">
                      <td className="p-3 font-medium">{stub.employee_name}</td>
                      <td className="p-3">{stub.period_start} - {stub.period_end}</td>
                      <td className="p-3 text-right font-mono">{(stub.hours_worked || 0).toFixed(1)}h</td>
                      <td className="p-3 text-right font-mono">${(stub.gross_pay || 0).toFixed(2)}</td>
                      <td className="p-3 text-right font-mono text-red-600">-${(stub.total_deductions || 0).toFixed(2)}</td>
                      <td className="p-3 text-right font-mono text-green-600 font-bold">${(stub.net_pay || 0).toFixed(2)}</td>
                      <td className="p-3 capitalize">{stub.payment_method || 'N/A'}</td>
                      <td className="p-3 text-xs text-slate-500">{fmtDate(stub.created_at)}</td>
                      <td className="p-3 text-center"><Button size="sm" variant="destructive" onClick={() => deletePayStub(stub.id, stub.employee_name)} disabled={deletingStub === stub.id} data-testid={`delete-stub-${stub.id}`}><Trash2 className="w-4 h-4" /></Button></td>
                    </tr>
                  ))}</tbody>
                </table>
                <p className="text-xs text-slate-500 mt-3">Total: {filteredPayStubs.length} talonario(s)</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payroll History */}
      {showHistory && filteredPayrollHistory.length > 0 && (
        <Card data-testid="payroll-history-section">
          <CardHeader><CardTitle className="flex items-center gap-2"><History className="w-5 h-5" /> Historial de Nominas Guardadas</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100"><tr><th className="text-left p-3">Periodo</th><th className="text-center p-3">Empleados</th><th className="text-right p-3">Total Bruto</th><th className="text-right p-3">Total Deducciones</th><th className="text-right p-3">Total Neto</th><th className="text-left p-3">Procesado Por</th><th className="text-left p-3">Fecha</th><th className="text-center p-3">Acciones</th></tr></thead>
                <tbody>{filteredPayrollHistory.map((run, idx) => (
                  <tr key={idx} className="border-b hover:bg-slate-50">
                    <td className="p-3 font-medium">{run.period_start} al {run.period_end}</td>
                    <td className="p-3 text-center">{run.employees?.length || 0}</td>
                    <td className="p-3 text-right font-mono">${(run.totals?.gross || 0).toFixed(2)}</td>
                    <td className="p-3 text-right font-mono text-red-600">-${(run.totals?.deductions || 0).toFixed(2)}</td>
                    <td className="p-3 text-right font-mono text-green-600 font-bold">${(run.totals?.net || 0).toFixed(2)}</td>
                    <td className="p-3">{run.processed_by_name || 'N/A'}</td>
                    <td className="p-3 text-xs text-slate-500">{fmtDate(run.processed_at)}</td>
                    <td className="p-3 text-center"><Button size="sm" variant="outline" onClick={() => loadSavedPayroll(run)} data-testid={`load-payroll-${idx}`}>Cargar</Button></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Period Selection */}
      <Card data-testid="payroll-period-card">
        <CardHeader><CardTitle className="flex items-center gap-2"><Calculator className="w-5 h-5" /> Periodo de Pago</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div><Label>Fecha Inicio</Label><Input type="date" value={payPeriod.start} onChange={(e) => setPayPeriod({...payPeriod, start: e.target.value})} data-testid="payroll-start-date" /></div>
            <div><Label>Fecha Fin</Label><Input type="date" value={payPeriod.end} onChange={(e) => setPayPeriod({...payPeriod, end: e.target.value})} data-testid="payroll-end-date" /></div>
            <Button onClick={calculatePayroll} disabled={processing} className="bg-blue-600 hover:bg-blue-700" data-testid="calculate-payroll-btn">
              <Calculator className="w-4 h-4 mr-2" /> {processing ? 'Calculando...' : 'Calcular Nomina'}
            </Button>
            {payrollData.length > 0 && (
              <Button onClick={savePayroll} disabled={saving} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700" data-testid="save-payroll-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Guardando...' : 'Guardar Nomina'}
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-500 mt-2">Las horas trabajadas se obtienen automaticamente de los ponches del empleado</p>
        </CardContent>
      </Card>

      {/* Settings Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-blue-50"><CardContent className="p-4"><p className="text-xs text-blue-600">Hacienda</p><p className="text-xl font-bold text-blue-800">{payrollSettings.hacienda_percent || 0}%</p></CardContent></Card>
        <Card className="bg-blue-50"><CardContent className="p-4"><p className="text-xs text-blue-600">Seguro Social</p><p className="text-xl font-bold text-blue-800">{payrollSettings.social_security_percent || 6.2}%</p></CardContent></Card>
        <Card className="bg-blue-50"><CardContent className="p-4"><p className="text-xs text-blue-600">Medicare</p><p className="text-xl font-bold text-blue-800">{payrollSettings.medicare_percent || 1.45}%</p></CardContent></Card>
        <Card className="bg-amber-50"><CardContent className="p-4"><p className="text-xs text-amber-600">Contratistas</p><p className="text-xl font-bold text-amber-800">{payrollSettings.contractor_percent || 10}%</p></CardContent></Card>
        <Card className="bg-slate-50 cursor-pointer hover:bg-slate-100" onClick={() => { setSettingsForm({...payrollSettings}); setEditingSettings(!editingSettings); }}>
          <CardContent className="p-4 text-center"><p className="text-xs text-slate-600">Configurar</p><Edit2 className="w-5 h-5 mx-auto mt-1 text-slate-500" /></CardContent>
        </Card>
      </div>

      {/* Settings Editor */}
      {editingSettings && (
        <Card data-testid="payroll-settings-editor">
          <CardHeader><CardTitle>Configuracion de Deducciones</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><Label>Hacienda %</Label><Input type="number" step="0.01" value={settingsForm.hacienda_percent || 0} onChange={(e) => setSettingsForm({...settingsForm, hacienda_percent: parseFloat(e.target.value) || 0})} /></div>
              <div><Label>Seguro Social %</Label><Input type="number" step="0.01" value={settingsForm.social_security_percent || 6.2} onChange={(e) => setSettingsForm({...settingsForm, social_security_percent: parseFloat(e.target.value) || 0})} /></div>
              <div><Label>Medicare %</Label><Input type="number" step="0.01" value={settingsForm.medicare_percent || 1.45} onChange={(e) => setSettingsForm({...settingsForm, medicare_percent: parseFloat(e.target.value) || 0})} /></div>
              <div><Label>Contratistas %</Label><Input type="number" step="0.01" value={settingsForm.contractor_percent || 10} onChange={(e) => setSettingsForm({...settingsForm, contractor_percent: parseFloat(e.target.value) || 0})} /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={saveSettings} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700" data-testid="save-settings-btn"><Save className="w-4 h-4 mr-2" /> Guardar</Button>
              <Button variant="outline" onClick={() => setEditingSettings(false)}>Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payroll Results */}
      {payrollData.length > 0 && (
        <Card data-testid="payroll-results">
          <CardHeader><CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Resultado de Nomina ({payrollData.length} empleados)</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100"><tr>
                  <th className="text-left p-3">Empleado</th><th className="text-left p-3">Tipo</th><th className="text-center p-3">Exento</th><th className="text-center p-3">Horas</th>
                  <th className="text-right p-3">Tarifa/Salario</th><th className="text-center p-3">Ret. Hacienda</th><th className="text-right p-3">Bruto</th><th className="text-right p-3">Deducciones</th><th className="text-right p-3">Neto</th><th className="text-center p-3">Metodo</th>
                </tr></thead>
                <tbody>{payrollData.map((p, idx) => (
                  <tr key={idx} className={`border-b hover:bg-slate-50 ${!p.hasPayConfig ? 'bg-yellow-50' : ''} ${p.isExemptFromDeduction ? 'bg-green-50' : ''}`}>
                    <td className="p-3"><p className="font-medium">{p.employee.name}</p><p className="text-xs text-slate-500">{p.employee.profile?.position || 'Sin posicion'}</p></td>
                    <td className="p-3"><Badge className={p.isContractor ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}>{p.isContractor ? 'Contratista' : 'Empleado'}</Badge></td>
                    <td className="p-3 text-center"><Checkbox checked={p.isExemptFromDeduction} onCheckedChange={(c) => toggleContractorExemption(idx, c)} disabled={p.grossPay <= 0} /></td>
                    <td className="p-3 text-center">
                      {p.isHourly || !p.hasPayConfig ? (
                        editingHours === idx ? (
                          <div className="flex items-center gap-1 justify-center">
                            <Input type="number" step="0.01" defaultValue={p.hoursWorked} className="w-20 h-8 text-center" onKeyDown={(e) => { if (e.key === 'Enter') updateEmployeeHours(idx, e.target.value); if (e.key === 'Escape') setEditingHours(null); }} autoFocus />
                            <Button size="sm" variant="ghost" onClick={(e) => { const input = e.currentTarget.previousSibling; updateEmployeeHours(idx, input.value); }}><Check className="w-4 h-4 text-green-600" /></Button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center gap-1">
                            <Clock className="w-4 h-4 text-slate-400" />
                            <span className={`font-mono ${p.hoursWorked === 0 ? 'text-slate-400' : ''}`}>{p.hoursWorked.toFixed(2)}</span>
                            <Button size="sm" variant="ghost" onClick={() => setEditingHours(idx)}><Edit2 className="w-3 h-3 text-slate-400" /></Button>
                          </div>
                        )
                      ) : <span className="text-slate-400">Fijo</span>}
                    </td>
                    <td className="p-3 text-right font-mono text-xs">{p.isHourly ? `$${p.hourlyRate.toFixed(2)}/hr` : p.fixedSalary > 0 ? `$${p.fixedSalary.toFixed(2)}` : <span className="text-yellow-600">$0.00</span>}</td>
                    <td className="p-3 text-center font-mono text-xs" data-testid={`tax-retention-${idx}`}>{p.isContractor ? <span className="text-slate-400">N/A</span> : <span className={p.taxRetentionPercent > 0 ? 'text-orange-600 font-medium' : 'text-slate-400'}>{p.taxRetentionPercent || 0}%</span>}</td>
                    <td className="p-3 text-right font-mono">{p.grossPay > 0 ? `$${p.grossPay.toFixed(2)}` : <span className="text-slate-400">$0.00</span>}</td>
                    <td className="p-3 text-right"><p className={`font-mono ${p.totalDeductions > 0 ? 'text-red-600' : 'text-slate-400'}`}>-${p.totalDeductions.toFixed(2)}</p>
                      {p.totalDeductions > 0 && <div className="text-xs text-slate-500">{Object.entries(p.deductions).map(([k, v]) => <span key={k} className="mr-2">{k}: ${v.toFixed(2)}</span>)}</div>}
                    </td>
                    <td className="p-3 text-right font-mono font-bold text-green-600">{p.netPay > 0 ? `$${p.netPay.toFixed(2)}` : <span className="text-slate-400">$0.00</span>}</td>
                    <td className="p-3 text-center">
                      <Select value={p.paymentMethod || 'check'} onValueChange={(v) => updatePaymentMethod(idx, v)}>
                        <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="cash">Efectivo</SelectItem><SelectItem value="check">Cheque</SelectItem><SelectItem value="direct_deposit">Deposito</SelectItem></SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}</tbody>
                <tfoot className="bg-slate-100 font-bold"><tr>
                  <td colSpan="3" className="p-3">TOTALES</td>
                  <td className="p-3 text-center font-mono">{payrollData.reduce((a, p) => a + (p.isHourly ? p.hoursWorked : 0), 0).toFixed(2)} hrs</td>
                  <td className="p-3"></td>
                  <td className="p-3"></td>
                  <td className="p-3 text-right font-mono">${payrollData.reduce((a, p) => a + p.grossPay, 0).toFixed(2)}</td>
                  <td className="p-3 text-right font-mono text-red-600">-${payrollData.reduce((a, p) => a + p.totalDeductions, 0).toFixed(2)}</td>
                  <td className="p-3 text-right font-mono text-green-600">${payrollData.reduce((a, p) => a + p.netPay, 0).toFixed(2)}</td>
                  <td className="p-3"></td>
                </tr></tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {payrollData.length === 0 && (
        <Card><CardContent className="p-8 text-center text-slate-500">
          <Calculator className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="font-medium">{employees.length} empleados disponibles</p>
          <p className="text-sm">Selecciona un periodo y haz clic en "Calcular Nomina" para ver los resultados</p>
        </CardContent></Card>
      )}
    </div>
  );
};

export default PayrollTab;
